// Fotocamera con riquadro guida + conversione B/N ad alto contrasto (per l'AI)
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, X, Check, Upload } from "lucide-react";

export default function WmsCamera({ open, onClose, onDone, multi = false, title = "Scansiona" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [shots, setShots] = useState([]);
  const [cameraOk, setCameraOk] = useState(true);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    setShots([]);
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 } } })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraOk(true);
      })
      .catch(() => setCameraOk(false));
    return () => {
      window.removeEventListener("keydown", onKey);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toBw = useCallback((source, w, h) => new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.filter = "grayscale(1) contrast(1.5) brightness(1.08)";
    ctx.drawImage(source, 0, 0, w, h);
    canvas.toBlob((blob) => resolve({ blob, url: URL.createObjectURL(blob) }), "image/jpeg", 0.85);
  }), []);

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const shot = await toBw(v, v.videoWidth, v.videoHeight);
    setShots((s) => (multi ? [...s, shot] : [shot]));
    if (!multi) finish([shot]);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const news = [];
    for (const f of files) {
      const img = new Image();
      const url = URL.createObjectURL(f);
      await new Promise((res) => { img.onload = res; img.src = url; });
      news.push(await toBw(img, img.naturalWidth, img.naturalHeight));
      URL.revokeObjectURL(url);
    }
    const all = multi ? [...shots, ...news] : news.slice(0, 1);
    setShots(all);
    if (!multi && all.length) finish(all);
  };

  const finish = (list) => {
    onDone((list || shots).map((s) => s.blob));
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="wms-camera">
      <div className="flex items-center justify-between px-4 h-14 bg-zinc-950 text-white shrink-0">
        <p className="font-['Chivo'] font-black uppercase">{title}</p>
        <button onClick={onClose} className="w-12 h-12 flex items-center justify-center hover:bg-zinc-800" data-testid="camera-close"><X className="w-6 h-6" /></button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {cameraOk ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
            {/* riquadro guida */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="relative w-[86%] h-[68%]">
                <div className="absolute inset-0 border-2 border-[#FF4500] shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
                {[["top-0 left-0", "border-t-4 border-l-4"], ["top-0 right-0", "border-t-4 border-r-4"], ["bottom-0 left-0", "border-b-4 border-l-4"], ["bottom-0 right-0", "border-b-4 border-r-4"]].map(([pos, b], i) => (
                  <div key={i} className={`absolute ${pos} w-10 h-10 ${b} border-white`} />
                ))}
                <p className="absolute -top-8 left-0 right-0 text-center text-white text-sm font-bold uppercase tracking-wide">Inquadra il documento dentro il riquadro</p>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 gap-4 p-6 text-center">
            <Camera className="w-14 h-14" />
            <p className="font-bold">Fotocamera non disponibile su questo dispositivo.</p>
            <p className="text-sm text-zinc-500">Usa "Carica foto" qui sotto (la conversione B/N viene fatta comunque).</p>
          </div>
        )}
      </div>

      {shots.length > 0 && (
        <div className="flex gap-2 px-4 py-2 bg-zinc-950 overflow-x-auto shrink-0" data-testid="camera-shots">
          {shots.map((s, i) => (
            <div key={i} className="relative shrink-0">
              <img src={s.url} alt="" className="h-16 w-24 object-cover border-2 border-zinc-700" />
              <button onClick={() => setShots(shots.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white w-6 h-6 text-xs font-bold">×</button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 p-3 bg-zinc-950 shrink-0">
        <button onClick={() => fileRef.current?.click()} className="h-16 border-2 border-zinc-700 text-zinc-300 font-bold uppercase text-xs flex flex-col items-center justify-center gap-1" data-testid="camera-upload">
          <Upload className="w-5 h-5" /> Carica foto
        </button>
        <button onClick={capture} disabled={!cameraOk} className="h-16 bg-[#FF4500] text-white font-black uppercase flex items-center justify-center gap-2 disabled:opacity-30" data-testid="camera-shoot">
          <Camera className="w-6 h-6" /> Scatta
        </button>
        {multi ? (
          <button onClick={() => finish()} disabled={!shots.length} className="h-16 bg-green-600 text-white font-black uppercase text-sm flex items-center justify-center gap-1 disabled:opacity-30" data-testid="camera-done">
            <Check className="w-5 h-5" /> Analizza ({shots.length})
          </button>
        ) : (
          <div className="h-16 flex items-center justify-center text-zinc-500 text-xs font-bold uppercase">1 foto = analisi</div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple={multi} className="hidden" onChange={onFiles} data-testid="camera-file-input" />
      </div>
    </div>
  );
}
